import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { fetchWithAuth } from "../utils/fetchWithAuth";

const SPECIAL_EMAIL = "ratnanjali@islorg.com";
const formatStatus = status => (typeof status === "string" ? status.split("_").join(" ") : "");

export default function TrainChatbot() {
  const user = useMemo(() => JSON.parse(localStorage.getItem("user")), []);
  const isAuthorized = user?.email === SPECIAL_EMAIL;

  const [ingestions, setIngestions] = useState([]);
  const [loadingIngestions, setLoadingIngestions] = useState(true);
  const [listError, setListError] = useState("");

  const [folderId, setFolderId] = useState("");
  const [folderName, setFolderName] = useState("");
  const [ingestStatus, setIngestStatus] = useState({ busy: false, message: "" });
  const [selectedIngestionId, setSelectedIngestionId] = useState("");
  const [progressState, setProgressState] = useState({ loading: false, error: "", data: null });

  const fetchIngestions = useCallback(async () => {
    // const response = await fetchWithAuth("/api/drive_rag/list_ingestions");
    const response = await fetchWithAuth("/api/drive_rag/new/list-ingestions");
    if (!response) throw new Error("No response");
    if (response.status === 404) return [];
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || "Unable to fetch ingestions");
    }
    const data = await response.json().catch(() => ({}));
    return Array.isArray(data?.ingestions) ? data.ingestions : [];
  }, []);

  const loadIngestions = useCallback(async () => {
    if (!isAuthorized) return;
    setLoadingIngestions(true);
    setListError("");
    try {
      const items = await fetchIngestions();
      setIngestions(items);
      setSelectedIngestionId(current => {
        if (current && items.some(item => (item.ingestion_id || item.document_id || "") === current)) {
          return current;
        }
        const first = items[0];
        if (!first) return "";
        return first.ingestion_id || first.document_id || "";
      });
    } catch (error) {
      setListError(error.message || "Failed to load ingestions");
      setIngestions([]);
    } finally {
      setLoadingIngestions(false);
    }
  }, [fetchIngestions, isAuthorized]);

  useEffect(() => {
    loadIngestions();
  }, [loadIngestions]);

  const refreshIngestions = async () => {
    await loadIngestions();
  };

  const handleRetry = async (ing) => {
    const confirmRetry = window.confirm(`Are you sure you want to retry ingestion for "${ing.folder_name || ing.ingestion_id || ing.document_id}"?`);
    if (!confirmRetry) return;

    setIngestStatus({ busy: true, message: "Retrying..." });
    try {
      const ingestionId = ing.ingestion_id || ing.document_id || "";
      const queryParams = {
        folder_id: ing.folder_id,
        folder_name: ing.folder_name,
        overwrite_id: ingestionId
      };

      const query = new URLSearchParams(queryParams).toString();
      const response = await fetchWithAuth(`/api/drive_rag/new/ingest?${query}`, {
        method: "POST"
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Retry failed");
      }

      setIngestStatus({ busy: false, message: "Retry request accepted." });
      setTimeout(() => setIngestStatus(prev => ({ ...prev, message: "" })), 4000);
      refreshIngestions();
    } catch (error) {
      setIngestStatus({ busy: false, message: error.message || "Retry failed" });
      alert(`Failed to retry: ${error.message}`);
    }
  };

  const fetchIngestionProgress = useCallback(async ingestionId => {
    if (!ingestionId) return null;
    const response = await fetchWithAuth(`/api/drive_rag/new/ingestions/${encodeURIComponent(ingestionId)}`);
    if (!response) throw new Error("No response");
    if (response.status === 404) {
      throw new Error("Ingestion not found.");
    }
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || "Unable to fetch ingestion progress.");
    }
    const data = await response.json().catch(() => ({}));
    return data?.ingestion ?? null;
  }, []);

  const loadProgress = useCallback(
    async ingestionId => {
      if (!ingestionId) {
        setProgressState({ loading: false, error: "", data: null });
        return;
      }
      setProgressState(prev => ({ ...prev, loading: true, error: "" }));
      try {
        const ingestion = await fetchIngestionProgress(ingestionId);
        setProgressState({ loading: false, error: "", data: ingestion });
      } catch (error) {
        setProgressState({ loading: false, error: error.message || "Failed to fetch progress.", data: null });
      }
    },
    [fetchIngestionProgress]
  );

  useEffect(() => {
    if (!selectedIngestionId) return;
    loadProgress(selectedIngestionId);
  }, [loadProgress, selectedIngestionId]);

  const handleIngest = async (event) => {
    event.preventDefault();
    if (!folderId.trim() || !folderName.trim()) {
      setIngestStatus({ busy: false, message: "Folder ID and name are required." });
      return;
    }

    // Check for existing ingestion
    const existing = ingestions.find(ing => ing.folder_id === folderId.trim() || ing.folder_name === folderName.trim());
    let overwriteId = "";
    if (existing) {
      const confirmed = window.confirm(`This folder or name ("${existing.folder_name}") has already been ingested. Do you want to overwrite the existing entry? Click Cancel to enter a different folder ID.`);
      if (!confirmed) {
        return; // User cancelled
      }
      overwriteId = existing.ingestion_id || existing.document_id || "";
    }

    setIngestStatus({ busy: true, message: "" });
    try {
      const queryParams = {
        folder_id: folderId.trim(),
        folder_name: folderName.trim()
      };
      if (overwriteId) queryParams.overwrite_id = overwriteId;

      const query = new URLSearchParams(queryParams).toString();
      // const response = await fetchWithAuth(`/api/drive_rag/ingest?${query}`, {
      const response = await fetchWithAuth(`/api/drive_rag/new/ingest?${query}`, {
        method: "POST"
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Ingestion failed");
      }
      const result = await response.json().catch(() => ({}));
      setIngestStatus({ busy: false, message: "Ingestion request accepted." });
      setFolderId("");
      setFolderName("");

      if (result.ingestion_id) {
        setSelectedIngestionId(result.ingestion_id);
      }

      setTimeout(() => setIngestStatus(prev => ({ ...prev, message: "" })), 4000);
      setTimeout(refreshIngestions, 1500);
    } catch (error) {
      setIngestStatus({ busy: false, message: error.message || "Ingestion failed" });
    }
  };

  if (!isAuthorized) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="flex h-full flex-col gap-8 overflow-y-auto bg-slate-50 px-6 py-8 text-slate-700 md:px-10">
      <header>
        <h1 className="text-3xl font-semibold text-slate-900">Train Chatbot</h1>
        <p className="mt-2 text-sm text-slate-500">
          Manage Drive RAG ingestions. Only ratnanjali@islorg.com can access these controls.
        </p>
      </header>

      <section className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-800">Ingestion Jobs</h2>
            <button
              type="button"
              onClick={refreshIngestions}
              className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-medium text-slate-500 transition hover:border-purple-200 hover:text-purple-600"
            >
              Refresh
            </button>
          </div>
          <div className="mt-4 space-y-3">
            {loadingIngestions ? (
              <p className="text-sm text-slate-500">Loading ingestions...</p>
            ) : listError ? (
              <p className="text-sm text-red-500">{listError}</p>
            ) : ingestions.length === 0 ? (
              <p className="text-sm text-slate-500">No ingestions found yet.</p>
            ) : (
              <ul className="space-y-3">
                {ingestions.map(ing => {
                  const ingestionId = ing.ingestion_id || ing.document_id || "";
                  return (
                    <li key={ingestionId} className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm shadow-sm">
                      <p className="font-semibold text-slate-700">{ing.folder_name || ingestionId}</p>
                      <p className="text-xs text-slate-500 break-all">{ingestionId}</p>
                      {ing.created_at && (
                        <p className="text-xs text-slate-400">Created {new Date(ing.created_at).toLocaleString()}</p>
                      )}
                      {ing.performing_user && (
                        <p className="text-xs text-slate-400">By {ing.performing_user}</p>
                      )}
                      {typeof ing.is_searchable !== "undefined" && (
                        <p className="text-xs text-slate-400">Searchable: {ing.is_searchable ? "Yes" : "No"}</p>
                      )}
                      {ing.status === "FAILED" ? (
                        <div className="mt-2 rounded-lg bg-red-50 p-2 border border-red-100">
                          <p className="text-xs text-red-600 font-medium">Failed: {ing.progress?.message || "Unknown error"}</p>
                          <button
                            type="button"
                            onClick={() => handleRetry(ing)}
                            className="mt-2 rounded bg-red-100 px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-200"
                          >
                            Retry Ingestion
                          </button>
                        </div>
                      ) : ing.status === "PROCESSING" || ing.status === "QUEUED" ? (
                        <div className="mt-2 text-xs">
                          <p className="text-purple-600 font-medium">{formatStatus(ing.status)} {ing.progress?.percentage ? "- " + Math.round(ing.progress.percentage) + "%" : ""}</p>
                          <p className="text-slate-500 truncate" title={ing.progress?.message}>{ing.progress?.message}</p>
                          <div className="mt-1 h-1 w-full rounded-full bg-slate-100 overflow-hidden">
                            <div
                              className="h-full bg-purple-500 transition-all duration-500"
                              style={{ width: `${Math.min(Math.max(ing.progress?.percentage ?? 0, 0), 100)}%` }}
                            />
                          </div>
                        </div>
                      ) : ing.status ? (
                        <div className="mt-2">
                          <span className="inline-block rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-semibold text-green-700">
                            {formatStatus(ing.status)}
                          </span>
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-800">Start New Ingestion</h2>
          <p className="mt-2 text-xs text-slate-500">
            Provide the Google Drive folder identifier and a friendly name to trigger ingestion.
          </p>
          <form onSubmit={handleIngest} className="mt-4 space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Folder ID</label>
              <input
                type="text"
                value={folderId}
                onChange={event => setFolderId(event.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-200"
                placeholder="e.g. 1AbcDefGhijk"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Ingestion Name</label>
              <input
                type="text"
                value={folderName}
                onChange={event => setFolderName(event.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-200"
                placeholder="Friendly folder label"
              />
            </div>
            <button
              type="submit"
              disabled={ingestStatus.busy}
              className={`w-full rounded-lg px-4 py-2 text-sm font-medium text-white transition ${ingestStatus.busy
                ? "bg-purple-300"
                : "bg-purple-600 hover:bg-purple-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400"
                }`}
            >
              {ingestStatus.busy ? "Submitting..." : "Start Ingestion"}
            </button>
          </form>
          {ingestStatus.message && <p className="mt-3 text-xs text-slate-500">{ingestStatus.message}</p>}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-800">Ingestion Progress</h2>
              <p className="mt-1 text-xs text-slate-500">Select an ingestion to view real-time progress.</p>
            </div>
            <button
              type="button"
              onClick={() => loadProgress(selectedIngestionId)}
              disabled={!selectedIngestionId || progressState.loading}
              className={`rounded-lg border px-3 py-1 text-xs font-medium transition ${!selectedIngestionId
                ? "border-slate-100 text-slate-300"
                : "border-slate-200 text-slate-500 hover:border-purple-200 hover:text-purple-600"
                }`}
            >
              Refresh
            </button>
          </div>
          <div className="mt-4 space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Ingestion</label>
              <select
                value={selectedIngestionId}
                onChange={event => setSelectedIngestionId(event.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-200"
              >
                <option value="">Select ingestion</option>
                {ingestions.map(ing => {
                  const ingestionId = ing.ingestion_id || ing.document_id || "";
                  return (
                    <option key={`progress-${ingestionId}`} value={ingestionId}>
                      {ing.folder_name || ingestionId}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4 text-sm">
              {!selectedIngestionId ? (
                <p className="text-slate-500">Select an ingestion to view status.</p>
              ) : progressState.loading ? (
                <p className="text-slate-500">Loading progress...</p>
              ) : progressState.error ? (
                <p className="text-red-500">{progressState.error}</p>
              ) : progressState.data ? (
                <>
                  <p className="text-xs uppercase tracking-wide text-slate-500">
                    {formatStatus(progressState.data.status) || "In Progress"}
                  </p>
                  <p className="text-lg font-semibold text-slate-800">{progressState.data.folder_name || selectedIngestionId}</p>
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span>Progress</span>
                      <span>{Math.round(progressState.data.progress?.percentage ?? 0)}%</span>
                    </div>
                    <div className="mt-1 h-2 rounded-full bg-white">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 transition-[width]"
                        style={{ width: `${Math.min(Math.max(progressState.data.progress?.percentage ?? 0, 0), 100)}%` }}
                      />
                    </div>
                    {progressState.data.progress?.message && (
                      <p className="mt-2 text-xs text-slate-500">{progressState.data.progress.message}</p>
                    )}
                  </div>
                  <dl className="mt-4 space-y-2 text-xs text-slate-500">
                    <div className="flex justify-between">
                      <dt>Stage</dt>
                      <dd className="font-medium text-slate-700">{progressState.data.progress?.stage || "—"}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt>Files</dt>
                      <dd className="font-medium text-slate-700">
                        {progressState.data.progress?.processed_files ?? 0} / {progressState.data.progress?.total_files ?? 0}
                      </dd>
                    </div>
                    {progressState.data.progress?.current_file && (
                      <div>
                        <dt className="text-slate-500">Current file</dt>
                        <dd className="mt-1 break-words text-slate-700">{progressState.data.progress.current_file}</dd>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <dt>Requested By</dt>
                      <dd className="font-medium text-slate-700">{progressState.data.performing_user || "—"}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt>Updated</dt>
                      <dd className="font-medium text-slate-700">
                        {progressState.data.updated_at ? new Date(progressState.data.updated_at).toLocaleString() : "—"}
                      </dd>
                    </div>
                  </dl>
                </>
              ) : (
                <p className="text-slate-500">No progress data available yet.</p>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
